import {makeAutoObservable} from "mobx";
import {splitIntoSentences} from "../utils";


const processUrl = (url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.hostname;
    } catch {
        return url;
    }
};

export class PlagiarismStore {

    groupedResults = []
    resultsList = []
    activeGroupKey = null
    activeSubgroup = 1
    originalText = ""
    selectedSentences = []
    score = 0

    setScore(score) {
        this.score = score;
    }

    constructor(root) {
        this.root = root;
        makeAutoObservable(this);
    }

    setGroupedResults(plagiarismData, text) {
        plagiarismData.results = plagiarismData.results.map(result => ({
            ...result,
            link: result.link,
            shortLink: processUrl(result.link),
            sentence: (result).sentence,
            phrase: result.phrase,
            title: result.title
        }));

        const sources = {};
        const firstResult = plagiarismData.results[0];
        if (firstResult) {
            sources[firstResult.shortLink] = [];
        }
        for (const result of plagiarismData.results) {
            if (!sources[result.shortLink]) {
                sources[result.shortLink] = [];
            }
        }

        const sentences = splitIntoSentences(text);
        for (const sentence of sentences) {
            for (const source in sources) {
                const resultData = plagiarismData.results.filter((result) => result.shortLink === source);
                if (resultData) {
                    let isFound = false;
                    for (let i = 0; i < resultData.length; i++) {
                        const score = resultData[i];
                        if (score.scores[0].phrase.trim().toLowerCase().replace(/[^a-z]/g, '') === sentence.trim().toLowerCase().replace(/[^a-z]/g, '')) {
                            sources[source].push({
                                ...score.scores[0],
                                title: score.title,
                                link: score.link,
                                shortLink: score.shortLink
                            });
                            isFound = true;
                            break;
                        }
                    }
                    if (!isFound) {
                        sources[source].push({
                            score: 0,
                            texts: [],
                            sentence: [sentence],
                            shortLink: source,
                            title: resultData[0].title,
                            link: resultData[0].link,
                        });
                    }
                }
            }
        }
        this.setGroupedResultsState(Object.entries(sources).map(([link, results]) => {
            const baseResult = results[0];
            const subBlockData = results;
            if (subBlockData.length === 0) {
                return null;
            }
            let subblocksData = [];
            let localTexts = [];
            let localSentence = [];
            let localProbability = -1;
            for (const score of subBlockData) {
                if (Math.round(localProbability * 100) !== Math.round(score.score * 100)) {
                    subblocksData.push({probability: localProbability, texts: localTexts, shortLink: link, sentence: localSentence});
                    localTexts = [];
                    localSentence = [];
                    localProbability = score.score;
                }
                localTexts.push(score.phrase);
                localSentence.push(score.sentence);
            }
            if (localTexts.length > 0) {
                subblocksData.push({probability: localProbability, texts: localTexts, shortLink: link, sentence: localSentence});
            }
            subblocksData = subblocksData.slice(1, subblocksData.length);
            subblocksData = subblocksData.filter(block => block.probability > 0);
            return {
                ...baseResult,
                scores: subblocksData,
                score: Math.round(
                    subblocksData.reduce((sum, score) => sum + (score.probability * score.texts.join('').length), 0)  * 100
                    /
                    text.length
                ),
                title: baseResult.title
            };
        }).filter(Boolean).sort((a, b) => b.score - a.score));
    }

    setGroupedResultsState(groupedResults) {
        const totalLength = groupedResults.reduce((sum, result) => {
            var length = 0;
            for (const sentence of result.scores) {
                length += sentence.sentence.join('').length;
            }
            return sum + length;
        }, 0);

        if (groupedResults.length === 0 || totalLength === 0) {
            this.setScore(0);
        } else {
            const weightedScore = groupedResults.reduce((sum, result) => {
                var sentenceLength = 0;
                for (const sentence of result.scores) {
                    sentenceLength += sentence.sentence.join('').length;
                }
                const weight = sentenceLength / totalLength;
                return sum + (result.score * weight);
            }, 0);

            this.setScore(groupedResults.map((e) => e.score).reduce((a, b) => Math.max(a, b), 0));
        }

        this.groupedResults = groupedResults
    }

    setActiveGroup(shortLink) {
        this.activeGroupKey = shortLink;
    }

    incrementActiveSubgroup() {
        const prev = this.activeSubgroup;
        this.activeSubgroup = Math.min(prev + 1, this.activeGroup?.scores.length || 0);
    }

    decrementActiveSubgroup() {
        const prev = this.activeSubgroup;
        this.activeSubgroup = Math.max(1, prev - 1);
    }

    incrementActiveGroup() {
        if (!this.activeGroupKey || this.groupedResults.length === 0) return;

        const currentIndex = this.groupedResults.findIndex(result => result.shortLink === this.activeGroupKey);
        if (currentIndex === -1) return;

        const nextIndex = (currentIndex + 1) % this.groupedResults.length;
        this.activeGroupKey = (this.groupedResults[nextIndex].shortLink);
    }

    decrementActiveGroup() {
        if (!this.activeGroupKey || this.groupedResults.length === 0) return;

        const currentIndex = this.groupedResults.findIndex(result => result.shortLink === this.activeGroupKey);
        if (currentIndex === -1) return;

        const prevIndex = (currentIndex - 1 + this.groupedResults.length) % this.groupedResults.length;
        this.activeGroupKey = (this.groupedResults[prevIndex].shortLink);
    }

    get activeGroup() {
        return this.activeGroupKey ? this.groupedResults.find(result => result.shortLink === this.activeGroupKey) || null : null;
    }

    get groupedResultsSize() {
        return this.groupedResults.length
    }

    setOriginalText(text) {
        this.originalText = text
    }

    setSelectedSentences(sentences) {
        this.selectedSentences = sentences
    }

    setActiveSubgroup(index) {
        this.activeSubgroup = index
    }
}
